// i18n
import { translateInComponent } from 'infras/i18n';
import translations from 'presentations/views/components/pages/apps/CalendarYearPage/i18n.json';

import * as React from 'react';
import { imagePath } from 'presentations/assets';
import { GetServerSideProps } from 'next';
import CalendarTopPage from 'presentations/views/components/pages/apps/CalendarTopPage';
import ErrorPage from 'presentations/views/components/pages/ErrorPage';
import { monthlyCalendar } from 'usecases/app_calendar';
import { gengoList, Gengo } from 'usecases/app_calendar/jp/gengo';
import { formatGengoYear } from 'usecases/app_calendar/jp/gengo/formatter';
import { setBreadcrumbs } from 'presentations/views/helpers/breadcrumbs';
import MainDocument from 'presentations/views/components/MainDocument';
import {
  appCalendarMonthFilterPath,
  appCalendarMonthPath,
  appCalendarPath,
  appCalendarYearFilterPath,
  appCalendarYearPath,
} from 'presentations/routes/path';
import { canonicalUrl } from 'presentations/routes/helper';
import {
  holidayList,
  holidayMasterList,
} from 'usecases/app_calendar/jp/holidays';
import {
  getLogo,
  getBreadcrumbs,
} from 'presentations/views/components/atoms/StructuredData';
import { getDateTime } from 'infras/datetime';
import i18next from 'i18next';

i18next.init({
  interpolation: {
    format: function (value, format) {
      if (format === 'uppercase') {
        return value.toUpperCase();
      }
      if (value instanceof Date && format) {
        return getDateTime(value, 'Asia/Tokyo').toFormat(format);
      }
      return value;
    },
  },
});

interface ServerProps {
  pageType: string;
  locale: string;
  calendars: any[];
  linkCanonical: string;
  gengo: Gengo;
  year: number;
  month: number;
  statusCode?: number;
  holidays: any;
  holidayMasters: any;
}

export const getServerSideProps: GetServerSideProps = async ({
  req,
  res,
  query,
  locale: serverLocale,
}: any) => {
  if (query.filter || query.display) {
    const filter = query.display ? 'moon' : 'taian';
    if (query.year && query.month) {
      res.redirect(
        appCalendarMonthFilterPath(
          query.year,
          query.month,
          filter,
          serverLocale,
        ),
      );
      return { props: { statusCode: 301 } };
    } else if (query.year) {
      res.redirect(appCalendarYearFilterPath(query.year, filter, serverLocale));
      return { props: { statusCode: 301 } };
    }
  } else {
    if (query.year && query.month) {
      res.redirect(appCalendarMonthPath(query.year, query.month, serverLocale));
      return { props: { statusCode: 301 } };
    } else if (query.year) {
      res.redirect(appCalendarYearPath(query.year, serverLocale));
      return { props: { statusCode: 301 } };
    }
  }
  const locale = 'ja';
  const today = getDateTime({}, 'Asia/Tokyo');
  const year = today.year;
  const month = today.month;
  const gengo = gengoList(year)[0];
  const calendars = [];
  let holidays = {};
  for (let i = 0; i < 12; i++) {
    const m = today.plus({ month: i });
    holidays = { ...holidays, ...holidayList(m.year) };
    calendars.push(monthlyCalendar(m.year, m.month, { locale, holidays }));
  }
  const holidayMasters = holidayMasterList();

  const path = appCalendarPath({
    year: Number(query.year),
    month: Number(query.month),
    display: Number(query.display),
  });
  const linkCanonical = canonicalUrl(req, path);
  const pageType = 'top';

  const props: ServerProps = {
    pageType,
    locale,
    linkCanonical,
    calendars,
    gengo,
    year,
    month,
    holidays,
    holidayMasters,
  };
  return { props };
};

const Page = (props: ServerProps) => {
  if (props.statusCode) {
    return <ErrorPage statusCode={props.statusCode} />;
  }
  const t = translateInComponent(translations);

  const date = new Date(props.year, props.month - 1, 1);

  const breadcrumbs = [];
  breadcrumbs.push({
    link: appCalendarPath(),
    title: t('calendar'),
  });
  if (props.pageType === 'year' || props.pageType === 'month') {
    breadcrumbs.push({
      link: appCalendarYearPath(props.year),
      title: t('date.year', { date }),
    });
  }
  if (props.pageType === 'month') {
    breadcrumbs.push({
      link: appCalendarMonthPath(props.year, props.month),
      title: t('date.month', { date }),
    });
  }
  let gengo = t('gengoTitle', {
    year: formatGengoYear(props.year - props.gengo.start + 1),
    name: props.gengo.name,
  });

  let title;
  const calendar = t('calendar');
  if (props.pageType === 'year') {
    title = t('titleYear', {
      date,
      gengo: gengo,
      calendar,
    });
  } else if (props.pageType === 'top') {
    title = t('titleTop', { calendar, date });
  } else {
    title = t('title', {
      date,
      gengo: gengo,
      calendar,
    });
  }
  gengo = t('gengoTitleKana', {
    year: formatGengoYear(props.year - props.gengo.start + 1),
    name: props.gengo.name,
    kana: props.gengo.kana,
  });
  const description = t('description', {
    date,
    gengo: gengo,
    calendar,
  });

  const routes = setBreadcrumbs(breadcrumbs);
  const calendars = props.calendars.filter((c, i) => i !== 0);

  return (
    <MainDocument
      title={title}
      description={description}
      locales={['ja']}
      isShowDownloadLink
      linkCanonical={props.linkCanonical}
      disableGoogleTagManager={true}
      url={props.linkCanonical}
      image={imagePath(`apps/calendar/mv/${props.month}.jpg`)}
      structuredData={
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: getLogo() }}
          />
          {breadcrumbs.length > 0 && (
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: getBreadcrumbs({ items: breadcrumbs }),
              }}
            />
          )}
        </>
      }
      isUseAdsense
    >
      <CalendarTopPage
        mainCalendar={props.calendars[0]}
        calendarName={calendar}
        calendars={calendars}
        gengo={props.gengo}
        year={props.year}
        month={props.month}
        routes={routes}
        holidays={props.holidays}
        holidayMasters={props.holidayMasters}
      />
    </MainDocument>
  );
};

export default Page;
